// Public giveaway T&Cs. Uses the existing SpotDrop dark aesthetic but stays
// intentionally light — no framer-motion, no data-fetch, no i18n. The page
// reads campaign start/end dates from /api/campaign/public so the same
// component works when the campaign is live or off. Prize wording is
// hard-coded to the current €100 launch campaign.

import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MapPin, Trophy } from "lucide-react";
import { fetchPublicCampaign } from "../lib/campaign";

const FALLBACK_DATE = "To be confirmed";

function formatDate(iso) {
  if (!iso) return FALLBACK_DATE;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return FALLBACK_DATE;
    return d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return FALLBACK_DATE;
  }
}

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="font-heading text-xl md:text-2xl font-semibold tracking-tight">{title}</h2>
      <div className="mt-3 text-sm md:text-base text-zinc-300 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-zinc-500 shrink-0">{label}</span>
      <span className="text-white">{value}</span>
    </div>
  );
}

export default function GiveawayTerms() {
  const [meta, setMeta] = React.useState(null);
  React.useEffect(() => {
    fetchPublicCampaign().then(setMeta);
  }, []);

  const start = formatDate(meta?.start_date);
  const end = formatDate(meta?.end_date);

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white" data-testid="giveaway-terms">
      <header className="px-5 py-5 md:px-10 md:py-6 border-b border-white/5 flex items-center justify-between max-w-4xl mx-auto">
        <Link to="/" className="flex items-center gap-2" data-testid="terms-back-brand">
          <div className="w-8 h-8 rounded-full bg-[#ccff00] flex items-center justify-center">
            <MapPin className="w-4 h-4 text-[#0a0a0c]" />
          </div>
          <span className="font-heading text-lg font-semibold tracking-tight">SpotDrop.</span>
        </Link>
        <Link
          to="/"
          className="text-xs text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-1.5"
          data-testid="terms-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to SpotDrop
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-10 md:px-10 md:py-14">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#ccff00]/40 bg-[#ccff00]/[0.06] text-[10px] uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
          <Trophy className="w-3 h-3" />
          Giveaway Terms
        </div>

        <h1 className="mt-5 font-heading text-3xl md:text-5xl font-semibold tracking-tight" data-testid="terms-title">
          SpotDrop €100 Fuel Giveaway — Terms &amp; Conditions
        </h1>
        <p className="mt-3 text-sm text-zinc-500">
          Last updated: 10 August 2026
        </p>

        <Section title="Campaign">
          <Row label="Name" value="SpotDrop €100 Fuel Giveaway" />
          <Row label="Prize pool" value="€100 total" />
          <Row label="Winners" value="2 winners × €50 fuel voucher each" />
          <Row label="Start date" value={start} />
          <Row label="End date" value={end} />
          <p className="text-zinc-400">
            No purchase is required. Participation is free. There is no mandatory SpotDrop
            account or password required to enter.
          </p>
        </Section>

        <Section title="Eligibility">
          <ul className="list-disc pl-5 space-y-2">
            <li>Open to residents of Malta aged 18 or over.</li>
            <li>To enter, a participant provides <strong>either</strong> a valid email address <strong>or</strong> an Instagram handle. Only one is required.</li>
            <li>Only one participant identity is accepted per device.</li>
            <li>SpotDrop staff and their direct family members are not eligible.</li>
          </ul>
          <p className="text-zinc-400 pt-2">
            A qualified referral is <strong>not required</strong> to be eligible for the
            prize draw. Referrals are one of several optional ways to earn additional
            entries and increase your chances of winning.
          </p>
        </Section>

        <Section title="How to earn entries">
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Save My Car</strong> — +1 entry per day (max one per calendar day per device).</li>
            <li><strong>Valid Drop</strong> — +2 entries per successful parking-spot drop.</li>
            <li><strong>Valid Claim</strong> — +2 entries per successful claim of a parking spot.</li>
            <li><strong>Qualified referral</strong> — +3 entries once your invited friend performs a valid Drop or Claim.</li>
            <li><strong>Install bonus</strong> — +1 entry, once only, when the SpotDrop app installation is reliably confirmed.</li>
            <li>Simply opening a referral link or sharing it earns <strong>no</strong> entries.</li>
            <li>A referral only qualifies after the invited person performs a valid qualifying action inside SpotDrop.</li>
          </ul>
        </Section>

        <Section title="Fair play">
          <ul className="list-disc pl-5 space-y-2">
            <li>Duplicate, fraudulent, automated, scripted or otherwise abusive entries may be removed at any time without notice.</li>
            <li>Devices that are banned or flagged as suspicious by SpotDrop&apos;s anti-abuse systems may be excluded from the draw.</li>
            <li>Any attempt to inflate entries — including creating multiple identities on the same device — will disqualify those entries.</li>
          </ul>
        </Section>

        <Section title="Winner selection">
          <ul className="list-disc pl-5 space-y-2">
            <li>Winners are selected <strong>randomly</strong> from eligible entries.</li>
            <li>More entries increase your chances of being drawn but do not guarantee a prize.</li>
            <li>Winners will be contacted through the email address or Instagram handle submitted at entry.</li>
            <li>If a winner cannot be contacted within a reasonable period, SpotDrop may select another winner.</li>
            <li>SpotDrop may request reasonable proof of eligibility (e.g. Maltese residency) before delivering the prize.</li>
          </ul>
        </Section>

        <Section title="Privacy">
          <p>
            SpotDrop only collects the minimum information needed to run the campaign: an
            anonymous device identifier and the contact you provide (email or Instagram).
          </p>
          <p>
            The <strong>Save My Car</strong> feature stores your parked-car location on your
            own device only. The campaign backend receives an anonymous &ldquo;I saved my car
            today&rdquo; signal — <strong>no coordinates, no notes and no address are ever sent
            to the server</strong>.
          </p>
          <p>
            Contact details are used solely to notify winners. They are not sold, rented,
            or shared with third parties.
          </p>
        </Section>

        <Section title="Changes">
          <p>
            SpotDrop may amend these terms if required for legal, technical or fairness
            reasons. Material changes will be reflected on this page.
          </p>
        </Section>

        <div className="mt-12 pt-8 border-t border-white/5 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid="terms-footer-back"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to SpotDrop
          </Link>
        </div>
      </main>
    </div>
  );
}
